import { computed, ref } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { getArticleBySlug } from '@/shared/api/experience';
import type { ArticleResponse, PassionateGuide } from '@/shared/api/experience';
import type { ArticleContent } from '@/features/articleContent';
import type { Geo } from '@/shared/models/journal';
import { type RawContentToken, normalizeContentTokens } from '../components/tableOfContents.types';

const getResponseStatus = (error: unknown): number | undefined => {
  if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number') {
    return error.status;
  }

  return undefined;
};

export const useArticlePageData = (route: RouteLocationNormalizedLoaded) => {
  const data = ref<ArticleResponse | null>(null);
  const isLoading = ref(false);
  const isNotFound = ref(false);

  const getArticle = async () => {
    const { slug } = route.params as { slug: string };
    if (!slug) {
      return;
    }

    isNotFound.value = false;
    isLoading.value = true;

    try {
      const articleResponse = await getArticleBySlug({ path: { slug } });

      if (articleResponse.response.status === 404) {
        isNotFound.value = true;
        return;
      }

      const article = articleResponse.data;
      if (!article) {
        return;
      }

      if (article.redirect) {
        window.location.href = article.redirect;
        return;
      }

      data.value = article;
    } catch (error) {
      if (getResponseStatus(error) === 404) {
        isNotFound.value = true;
      }
    } finally {
      isLoading.value = false;
    }
  };

  const details = computed(() => data.value?.article_details);
  const isUgc = computed(() => details.value?.is_ugc ?? false);
  const titleWithoutSpecSymbols = computed(() => {
    const title = details.value?.title;
    return title ? title.replaceAll('&nbsp;', ' ').replaceAll('&#173;', '') : '';
  });
  const contentsTokens = computed(() =>
    normalizeContentTokens((details.value?.contents_tokens ?? []) as RawContentToken[]),
  );

  const articleAuthor = computed(() => {
    const author = details.value?.author;
    return author && author.id != null ? author : null;
  });

  const relatedExperiences = computed(() => data.value?.related_experiences);
  const firstExperience = computed(() => relatedExperiences.value?.results?.[0]);
  const experienceGeo = computed(() => firstExperience.value?.geo);

  const geo = computed((): Geo => {
    const experienceGeoData = experienceGeo.value;
    if (!experienceGeoData) {
      return { city: [], country: [], region: [] };
    }
    const addUrl = (items: { id?: number; name?: string; slug?: string }[]) =>
      items.map((item) => ({
        id: item.id ?? 0,
        name: item.name ?? '',
        slug: item.slug ?? '',
        url: item.slug ? `/experience/${item.slug}/` : '',
      }));
    return {
      city: addUrl(experienceGeoData.city || []),
      country: addUrl(experienceGeoData.country || []),
      region: addUrl(experienceGeoData.region || []),
    };
  });

  const selectedCity = computed(() => {
    const cityWidget = data.value?.city_exps_search_widget;
    const city = firstExperience.value?.geo?.city?.[0];
    const citySlug = cityWidget?.city_slug || city?.slug;
    const cityName = cityWidget?.city_name || city?.name;
    if (!citySlug || !cityName) {
      return undefined;
    }
    return { id: citySlug, title: cityName, selected: true, params: { slug: citySlug } };
  });

  const descriptionTokens = computed(() => (details.value?.description_tokens ?? []) as unknown as ArticleContent);

  /** Отдельный блок гидов внизу статьи. */
  const guidesSectionGuides = computed((): PassionateGuide[] => {
    const passionate = data.value?.passionate_guides;
    return Array.isArray(passionate) ? passionate : [];
  });

  const relatedArticles = computed(() => data.value?.related_articles ?? []);
  const hasRelatedGuides = computed(() => guidesSectionGuides.value.length > 0);
  const totalExperiencesCount = computed(() => relatedExperiences.value?.count ?? 0);

  return {
    data,
    isLoading,
    isNotFound,
    getArticle,
    isUgc,
    titleWithoutSpecSymbols,
    contentsTokens,
    articleAuthor,
    geo,
    selectedCity,
    descriptionTokens,
    guidesSectionGuides,
    relatedArticles,
    hasRelatedGuides,
    totalExperiencesCount,
  };
};
